import React, { useEffect, useState } from 'react'
import AuthLayout from '../../../layout/AuthLayout'
import { useDispatch, useSelector } from 'react-redux'
import { updateRoute } from '../../../store/dataRoute'
import { Badge, Box, Card, Center, Checkbox, Flex, Grid, Group, Loader, Paper, ScrollArea, Select, Stack, Text } from '@mantine/core'
import { IconMessage } from '@tabler/icons-react'
import dayjs from 'dayjs'
import DetailChat from '../../../component/pages/Chat/DetailChat'
import { getChatList } from '../../../services/chat'
import { useViewportSize } from '@mantine/hooks'
import { pusherSubscribe, pusherUnsubscribe } from '../../../plugins/pusher'

const defaultParams = {
  'page[limit]': 1000,
  'sort': '-created_at'
}

const defaultStatus = [
  {
    label: 'Open',
    value: 'open'
  },
  {
    label: 'Closed',
    value: 'closed'
  },
  {
    label: 'Progress',
    value: 'progress'
  },
  {
    label: 'AI Active',
    value: 'ai active'
  },
]

const Chat = () => {
  const { height } = useViewportSize()

  const dataLogin = useSelector(state => state.auth.authorization)
  const userAuth = dataLogin?.user

  const [parameter, setParameter] = useState(defaultParams)
  const [assignedToMe, setAssignedToMe] = useState(false)
  const [dataChatList, setDataChatList] = useState([])
  const [totalData, setTotalData] = useState(0)
  const [loadingDataChatList, setLoadingDataChatlist] = useState(false)
  const [chatIdSelected, setChatIdSelected] = useState(0)
  const [detailChat, setDetailChat] = useState(null)
  const [incomingMessage, setIncomingMessage] = useState(null)
  const dispatch = useDispatch()

  const getListChat = async () => {
    try {
      setLoadingDataChatlist(true)
      const response = await getChatList(parameter)
      const { data, meta } = response.data

      const countData = meta.pagination.total

      const remap = data.map(e => {
        const { attributes } = e

        // helper
        const handleGetUser = (attributes) => {
          const { created_by } = attributes
          const { attributes: user } = created_by
          return {
            id: +created_by.id,
            name: user.name,
            email: user.email,
            phone: user.phone,
          }
        }

        const handleGetStatus = (attributes) => {
          const { status } = attributes
          const pickStatus = status[0]
          return {
            id: pickStatus.id, // id 86 -> open, id 87 -> progress, id 88 -> closed gan
            name: pickStatus.name,
          }
        }

        const handleGetChateble = (attributes) => {
          const { chatable_id, chatable_type } = attributes
          if (chatable_id) {
            const key = chatable_type.replace(/App\\Entities\\/g, '')
            return {
              id: chatable_id,
              label: `${key} #${chatable_id}`,
              key,
            }
          }
          return {
            id: null,
            label: 'General',
            key: 'General'
          }
        }

        return {
          id: attributes.id,
          createdAt: attributes.created_at,
          updatedAt: attributes.updated_at,
          user: handleGetUser(attributes),
          status: handleGetStatus(attributes),
          chateble: handleGetChateble(attributes),
          unReadCount: attributes.unread_count,
        }
      })

      setDataChatList(remap)
      setTotalData(countData)
    } catch (error) {
      console.log(error)
      setTotalData(0)
    } finally {
      setLoadingDataChatlist(false)
    }
  }

  const handleChangeFilter = (name, val) => {
    setParameter((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const setChat = (val) => {
    setChatIdSelected(+val.id)
    setDetailChat(val)

    // clear unReadcount
    const remap = dataChatList.map(e => {
      const unReadCount = e.id === val.id ? 0 : e.unReadCount
      return { ...e, unReadCount }
    })
    setDataChatList(remap)
  }

  const clearSelectedChat = () => {
    setChatIdSelected(0)
    setDetailChat(null)
  }

  // --- INCOMING MESSAGE ---
  const handleIncomingMessage = (incomingMessage) => {
    if (detailChat?.id !== incomingMessage?.chat_id) {
      const checkChatListId = dataChatList.find(e => e.id === incomingMessage?.chat_id)

      if (checkChatListId) {
        const statusId = checkChatListId.status.id
        const isClosed = statusId === 88 // chat status closed had incoming message
        const isAiToCs =
          statusId === 92 // status ai active
          && ['/cs', 'chat /cs'].includes(incomingMessage?.message)

        if (isClosed || isAiToCs) {
          getListChat()
          return
        }

        const remap = dataChatList.reduce((acc, current) => {
          if (current.id === incomingMessage?.chat_id) {
            acc.unshift({
              ...current,
              unReadCount: current.unReadCount + 1,
            })
          }
          else {
            acc.push(current)
          }

          return acc
        }, [])

        setDataChatList(remap)
      } else {
        getListChat()
      }
    }
  }

  const mappingRoute = [
    {
      label: '/',
      route: null
    },
    {
      label: 'Chats',
      route: null
    }
  ]

  useEffect(() => {
    dispatch(updateRoute({ 'data': mappingRoute }))
    // eslint-disable-next-line
  }, [dispatch])

  useEffect(() => {
    getListChat()
    // eslint-disable-next-line
  }, [parameter])

  // REAL TIME MESSAGE
  useEffect(() => {
    if (userAuth?.id) {
      const channel = pusherSubscribe(`user-${userAuth?.id}`)
      channel.bind('message-latest', (data) => {
        const { data: incomingMessage, success } = data
        if (success)
          setIncomingMessage(incomingMessage)
      })
    }

    return () => {
      // ...until it disconnected
      pusherUnsubscribe(`user-${userAuth?.id}`)
    };
    // eslint-disable-next-line
  }, [parameter, userAuth])

  useEffect(() => {
    handleIncomingMessage(incomingMessage)
    // eslint-disable-next-line
  }, [incomingMessage])

  useEffect(() => {
    if (assignedToMe) {
      setParameter(prev => ({
        ...prev,
        [`filter[participants.user_id]`]: userAuth.id
      }))
    } else {
      setParameter(prev => {
        const { [`filter[participants.user_id]`]: _, ...rest } = prev
        return rest
      })
    }
    // eslint-disable-next-line
  }, [assignedToMe])

  // view
  const renderChatCard = ({data, selected}) => {
    return (
      <Card padding="md" radius="md" withBorder style={{ borderColor: selected ? 'red' : '#9fa4aa' }}>
        <Group
          justify='space-between'
          flex="1"
          align='center'
          style={{
            cursor: 'pointer',
            borderRadius: '12px',
            ...(selected ? {color: 'red'} : {color: '#9fa4aa'})
          }}
        >
          <Flex gap='sm' align='center'>
            <IconMessage color='red' />
            <Box>
              <Text fz={12} fw='600' lineClamp={2}>{data.chateble.label}</Text>
              <Text fz={10} fw='500' c={selected ? 'red.6' : 'gray.6'} lineClamp={2}>{data.user.name}</Text>
            </Box>
          </Flex>

          <Group gap='xs'>
            <Box>
              <Text fz={10} style={{ marginInlineStart: 'auto' }}>{dayjs(data.createdAt).format('DD MMMM YYYY')}</Text>
              <Badge
                size="sm"
                variant='light'
                color={data.status.id === 86 ? 'red'
                  : data.status.id === 87 ? 'green'
                    : data.status.id === 92 ? 'orange'
                      : 'gray'
                }
              >
                {data.status.name}
              </Badge>
            </Box>

            {
              data.unReadCount &&
              <Badge size="xs" color={'red'}>
                {data.unReadCount > 99 ? '99+' : data.unReadCount}
              </Badge>
            }
          </Group>
        </Group>
      </Card>
    )
  }

  const renderFilterStatus = () => {
    return (
      <Box mb={'sm'}>
        <Select
          name='statuses'
          value={parameter['statuses']}
          label='Status'
          data={defaultStatus}
          onChange={(val) => handleChangeFilter('statuses', val)}
          size='xs'
          clearable
        />

        <Checkbox
          mt={'sm'}
          label="Assigned to me"
          size='xs'
          checked={assignedToMe}
          onChange={(event) => setAssignedToMe(event.currentTarget.checked)}
        />

        <Group mb={10} gap='xs' mt={'sm'}>
          <Text fz={12}>Total Data Found: </Text>
          <Text fz={12} fw='bold'>{totalData} Chats </Text>
        </Group>
      </Box>
    )
  }

  const chatList = (dataChatList) => {
    if (dataChatList.length === 0 && !loadingDataChatList) {
      return (<Text c="dimmed" ta="center">No Data</Text>)
    } else {
      return dataChatList.map((e, index) => {
        return (
          <Box key={index} onClick={() => setChat(e)}>
            {renderChatCard({data: e, selected: +e.id === +chatIdSelected})}
          </Box>
        )
      })
    }
  }

  const renderDetailChat = (detailChat) => {
    return (
      detailChat
        ? <DetailChat dataChat={detailChat} reloadList={getListChat} clearSelectedChat={clearSelectedChat} incomingMessage={incomingMessage} />
        : <Center w='full' h={150}>
            <Stack justify='center' align='center'>
              <Text c='gray.6' fw={500}>Please choose and select chat at sidebar</Text>
            </Stack>
          </Center>
    )
  }

  const showLayoutChat = () => {
    return (
      <Grid gutter="xl" grow>
        <Grid.Col span='content'>
          <Paper shadow="xs" radius="md" withBorder p="sm" >
            {renderFilterStatus()}
            { loadingDataChatList && <Center my={'sm'}><Loader size={'sm'} type='dots' color='red' /></Center> }
            <ScrollArea h={height / 1.49} offsetScrollbars scrollbarSize={6} overscrollBehavior="contain">
              <Stack
                gap="sm"
                justify="flex-start"
              >
                {chatList(dataChatList)}
              </Stack>
            </ScrollArea>
          </Paper>
        </Grid.Col>
        <Grid.Col span={8}>
          {renderDetailChat(detailChat)}
        </Grid.Col>
      </Grid>
    )
  }

  return (
    <AuthLayout>
      <Box my={20}>
        <Text fw={500} size='xl'>Active Chats<span style={{ color: 'gray' }}>(On Development)</span></Text>
        <Box my={30}>
          {showLayoutChat()}
        </Box>
        
      </Box> 
    </AuthLayout>
  )
}

export default Chat